/**
 * Version Compare Component
 * 
 * Loads two document versions and displays them side-by-side in DiffView.
 */

import { useState, useEffect, useCallback } from 'react';
import { documentsApi } from '../../services/api';
import { DiffView } from './DiffView';
import type { AeroDocument, DocumentSection } from '../../types/document-schema';
import type { DocumentOperation } from '../../types/document-operations';
import { Button } from '@/components/ui';
import { logger } from '../../utils/logger';

/**
 * Compute section-level diffs between two document versions
 * and convert them to DocumentOperation objects for display.
 */
function computeSectionDiffOperations(
  doc1: AeroDocument,
  doc2: AeroDocument
): DocumentOperation[] {
  const ops: DocumentOperation[] = [];
  
  const doc1Map = new Map<string, DocumentSection>(doc1.body.map(s => [s.id, s]));
  const doc2Map = new Map<string, DocumentSection>(doc2.body.map(s => [s.id, s]));
  
  // Added sections (in doc2 but not doc1)
  for (const [id, section] of doc2Map) {
    if (!doc1Map.has(id)) {
      const idx = doc2.body.indexOf(section);
      const prevSection = idx > 0 ? doc2.body[idx - 1] : null;
      ops.push({
        op: 'insert_section_after',
        targetId: prevSection?.id || id,
        newSection: section,
        explanation: `Section "${section.number} ${section.title}" added`,
      });
    }
  }
  
  // Removed sections (in doc1 but not doc2)
  for (const [id, section] of doc1Map) {
    if (!doc2Map.has(id)) {
      ops.push({
        op: 'delete_section',
        targetId: id,
        allowRequired: false,
        explanation: `Section "${section.number} ${section.title}" removed`,
      });
    }
  }
  
  // Modified sections (exist in both, content differs)
  for (const [id, doc2Section] of doc2Map) {
    const doc1Section = doc1Map.get(id);
    if (!doc1Section) continue;
    
    const titleChanged = doc1Section.title !== doc2Section.title;
    const blocksChanged = JSON.stringify(doc1Section.blocks) !== JSON.stringify(doc2Section.blocks);
    
    if (titleChanged) {
      ops.push({
        op: 'update_section_title',
        targetId: id,
        newTitle: doc2Section.title,
        explanation: `Title: "${doc1Section.title}" → "${doc2Section.title}"`,
      });
    }
    
    if (blocksChanged) {
      const blockCountDiff = doc2Section.blocks.length - doc1Section.blocks.length;
      const description = blockCountDiff > 0
        ? `${blockCountDiff} block(s) added`
        : blockCountDiff < 0
        ? `${Math.abs(blockCountDiff)} block(s) removed`
        : 'Content modified';
      
      ops.push({
        op: 'update_text',
        targetId: doc2Section.blocks[0]?.id || id,
        newText: '',
        explanation: `${description} in "${doc2Section.title}"`,
      });
    }
  }
  
  return ops;
}

interface VersionCompareProps {
  projectId: string;
  documentNumber: string;
  version1: string;
  version2: string;
  onClose: () => void;
}

export function VersionCompare({
  projectId,
  documentNumber,
  version1,
  version2,
  onClose
}: VersionCompareProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [doc1, setDoc1] = useState<AeroDocument | null>(null);
  const [doc2, setDoc2] = useState<AeroDocument | null>(null);
  const [operations, setOperations] = useState<DocumentOperation[]>([]);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Load both versions in parallel
      const [response1, response2] = await Promise.all([
        documentsApi.get(projectId, documentNumber, { 
          includeContent: 'true', 
          revision: version1,
          format: 'json'
        }),
        documentsApi.get(projectId, documentNumber, { 
          includeContent: 'true', 
          revision: version2,
          format: 'json'
        })
      ]);

      if (!response1.jsonDocument || !response2.jsonDocument) {
        throw new Error('One or both versions are not available in JSON format');
      }

      setDoc1(response1.jsonDocument);
      setDoc2(response2.jsonDocument);
      
      // Compute section-level diffs between the two versions
      const diffOps = computeSectionDiffOperations(response1.jsonDocument, response2.jsonDocument);
      logger.debug('[VersionCompare] Computed diff operations:', diffOps.length);
      setOperations(diffOps);
      
    } catch (err: unknown) {
      logger.error('[VersionCompare] Failed to load versions:', err);
      const message = err instanceof Error ? err.message : 'Failed to load versions for comparison';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [projectId, documentNumber, version1, version2]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-card">
        <div className="text-center">
          <div className="loading mb-2">Loading versions...</div>
          <div className="text-sm text-muted-foreground">
            Comparing Rev {version1} and Rev {version2}
          </div>
        </div>
      </div>
    );
  }

  if (error || !doc1 || !doc2) {
    return (
      <div className="flex items-center justify-center h-full bg-card">
        <div className="text-center">
          <div className="error text-destructive mb-4">
            {error || 'Failed to load documents'}
          </div>
          <div className="flex gap-2 justify-center">
            <Button variant="secondary" size="sm" onClick={loadVersions}>
              Retry
            </Button>
            <Button variant="secondary" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-card">
      {/* Header */}
      <div className="flex-shrink-0 bg-muted border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Version Comparison
            </h3>
            <div className="text-xs text-muted-foreground mt-1">
              Rev {version1} vs Rev {version2}
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
          >
            Close Comparison
          </Button>
        </div>
      </div>
      
      {/* Diff View */}
      <div className="flex-1 overflow-hidden">
        <DiffView
          originalDocument={doc1}
          previewDocument={doc2}
          operations={operations}
          onCloseSplitView={onClose}
          showOperationsSidebar={false}
        />
      </div>
    </div>
  );
}
